import { useState } from "react";
import {
  Check, Star, Coins, Users, Eye,
  Megaphone, Clock,
} from "lucide-react";
import { CORE_PLANS as CORE, SPECTATOR_PLANS as SPECTATOR } from "@/lib/plans";

const RED = "#C8000F";
const GOLD = "#E6B35A";

const SoonBadge = () => (
  <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
    style={{ background: "rgba(230,179,90,0.15)", color: GOLD }}>
    <Clock className="w-2.5 h-2.5" /> Soon
  </span>
);

const SectionTitle = ({ kicker, title, sub }) => (
  <div className="text-center max-w-2xl mx-auto mb-10">
    {kicker && <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>{kicker}</div>}
    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">{title}</h2>
    {sub && <p className="mt-4 text-slate-400">{sub}</p>}
  </div>
);

const COMPARE = [
  ["Starting Price", "$2.99/mo", "Free", "$14.99/mo", "$29.99/mo"],
  ["Find / View Rodeos", "✓", "✓", "✓", "✓"],
  ["Competitors Included", "3–Unlimited", "1", "5", "15"],
  ["Rodeo Scheduling", "—", "Basic", "✓", "✓"],
  ["Rodeo Count", "—", "—", "✓", "✓"],
  ["Prepared Entries", "—", "1 cr", "2/mo · 30/yr", "Unlimited"],
  ["Draw Lookup", "—", "1 cr", "2/mo · 30/yr", "Unlimited"],
  ["Results Audit", "—", "50 cr", "30 / 20 cr", "Live & Unlimited"],
  ["Winnings Tracking", "—", "—", "✓", "✓"],
  ["AI Coach", "—", "Credits", "Credits", "✓"],
  ["Advanced performance tools", "—", "—", "—", "Soon"],
];

const CREDIT_COSTS = [
  ["Prepared Rodeo Entry", "1 credit"],
  ["Draw Lookup", "1 credit"],
  ["Follow Additional Competitor", "1 credit / month"],
  ["Trade Report", "1 credit"],
  ["Get Counts", "1 credit"],
  ["Results & Standings Audit (Rookie)", "50 credits"],
  ["Audit (Semi Pro monthly / annual)", "30 / 20 credits"],
  ["Audit (Professional)", "Unlimited live"],
];

const Cell = ({ v }) => (
  <td className="px-3 py-2.5 text-center text-sm border-b border-white/5">
    {v === "✓" ? <Check className="w-4 h-4 mx-auto" style={{ color: RED }} /> : v === "—" ? <span className="text-slate-600">—</span> : v === "Soon" ? <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: "rgba(230,179,90,0.15)", color: GOLD }}>Soon</span> : <span className="text-slate-300">{v}</span>}
  </td>
);

// The full credits-based pricing model, reused on the public Landing page.
// `onCta` fires when a user taps any plan button (Landing sends them to signup).
export default function PricingModel({ onCta = () => {}, id }) {
  const [yearly, setYearly] = useState(false);
  return (
    <div id={id} className="relative z-10" data-testid="pricing-model">
      {/* Spectator */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5" data-testid="pm-spectator">
        <SectionTitle kicker="Spectator" title="Follow the cowboys & cowgirls you care about"
          sub="You don't have to compete to use Rodeo. Pick your favourites and Rodeo tracks where they're entered, when they're up, draw position, results, standings and available run videos." />
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {SPECTATOR.map((s) => (
            <div key={s.name} data-testid={`pm-spectator-${s.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`relative rounded-2xl p-6 ${s.popular ? "border-2 bg-[#141A24]" : "border border-white/10 bg-[#121823]"}`}
              style={s.popular ? { borderColor: RED, boxShadow: "0 0 40px rgba(200,0,15,0.22)" } : {}}>
              {s.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full" style={{ background: RED }}><Star className="w-3 h-3" /> Most Popular</span>}
              <div className="flex items-center gap-2 text-slate-400"><Eye className="w-4 h-4" /><span className="text-xs uppercase tracking-wider font-semibold">Spectator</span></div>
              <h3 className="font-display text-2xl font-bold mt-1 text-white">{s.name}</h3>
              <div className="mt-3 flex items-end gap-1"><span className="font-display text-4xl font-extrabold text-white">{s.price}</span><span className="text-slate-400 text-sm mb-1.5">/mo</span></div>
              <p className="text-sm text-slate-300 mt-3">{s.desc}</p>
              <p className="text-xs text-slate-500 mt-2">{s.extra}</p>
              <button onClick={onCta} data-testid={`pm-spectator-cta-${s.name.toLowerCase().replace(/\s/g, "-")}`} className="mt-5 w-full font-bold py-2.5 rounded-xl text-white" style={{ background: s.popular ? RED : "rgba(255,255,255,0.08)" }}>Get Started Free</button>
            </div>
          ))}
        </div>
      </section>

      {/* Core tiers */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5" data-testid="pm-core">
        <SectionTitle kicker="Competitor plans" title="From free tools to a full performance platform"
          sub="Start free and pay only when you need more — or let Rodeo run your whole season." />
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-1 p-1 rounded-full border border-white/10 bg-[#141A24]">
            <button onClick={() => setYearly(false)} data-testid="pm-billing-monthly" className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${!yearly ? "text-white" : "text-slate-400"}`} style={!yearly ? { background: RED } : {}}>Monthly</button>
            <button onClick={() => setYearly(true)} data-testid="pm-billing-yearly" className={`text-sm font-semibold px-5 py-2 rounded-full transition-colors ${yearly ? "text-white" : "text-slate-400"}`} style={yearly ? { background: RED } : {}}>Yearly</button>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {CORE.map((t) => {
            const Icon = t.icon;
            const showYearly = yearly && t.yearly;
            return (
              <div key={t.name} data-testid={`pm-tier-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`relative flex flex-col h-full rounded-2xl p-6 ${t.popular ? "border-2 bg-[#141A24]" : "border bg-[#121823]"}`}
                style={{ borderColor: t.popular ? RED : "rgba(255,255,255,0.1)", boxShadow: t.popular ? "0 0 40px rgba(200,0,15,0.22)" : "none" }}>
                {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-white px-3 py-1 rounded-full" style={{ background: RED }}><Star className="w-3 h-3" /> Most Popular</span>}
                <div className="flex items-center gap-2" style={{ color: t.accent }}><Icon className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-semibold">{t.tag}</span></div>
                <h3 className="font-display text-2xl font-bold mt-1 text-white">{t.name}</h3>
                <p className="text-sm text-slate-400 mt-1 min-h-[40px]">{t.desc}</p>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-4xl font-extrabold text-white">{showYearly ? t.yearly.split("/")[0] : t.price}</span>
                  <span className="text-slate-400 text-sm mb-1.5">{t.price === "Free" ? "" : showYearly ? "/year" : t.unit}</span>
                </div>
                {t.yearly && !yearly && <p className="text-[11px] text-slate-500 mt-1">or {t.yearly}</p>}
                <button onClick={onCta} data-testid={`pm-tier-cta-${t.name.toLowerCase().replace(/\s/g, "-")}`} className="mt-4 w-full font-bold py-2.5 rounded-xl text-white" style={{ background: t.popular ? RED : t.price === "Free" ? "rgba(255,255,255,0.08)" : "rgba(200,0,15,0.85)" }}>{t.price === "Free" ? "Start Free" : `Choose ${t.name}`}</button>
                <ul className="mt-5 space-y-2.5">
                  {t.features.map((f) => (
                    <li key={f.label} className={`flex gap-2.5 text-sm ${f.head ? "text-slate-400 text-xs uppercase tracking-wide pt-1" : "text-slate-200"}`}>
                      {!f.head && <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: f.soon ? "#6B7280" : t.accent }} />}
                      <span className={f.soon ? "text-slate-400" : ""}>{f.label}</span>
                      {f.soon && <SoonBadge />}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-2"><Coins className="w-3.5 h-3.5" style={{ color: GOLD }} /> Credits & add-ons</div>
                  <ul className="space-y-1.5 text-[12px] text-slate-400">
                    {t.credits.map((c) => <li key={c} className="flex gap-2"><span style={{ color: GOLD }}>•</span>{c}</li>)}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Coming soon teaser (details intentionally withheld until shipped) */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5 bg-[#0E131C]" data-testid="pm-deep">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: RED }}>Coming soon</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">More on the way for Professional</h2>
          <p className="mt-4 text-slate-400">We're building the next wave of advanced performance tools for serious competitors. Pro members get them first — at no extra cost.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full" style={{ background: "rgba(230,179,90,0.12)", color: GOLD }}>
            <Clock className="w-4 h-4" /> Advanced performance tools — in development
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5" data-testid="pm-compare">
        <SectionTitle kicker="Compare" title="Every plan, side by side" />
        <div className="max-w-5xl mx-auto overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-[#141A24]">
                <th className="px-3 py-3 text-left text-xs uppercase tracking-wide text-slate-400 font-semibold">Feature</th>
                {["Spectator", "Rookie", "Semi Pro", "Professional"].map((h) => (
                  <th key={h} className="px-3 py-3 text-center text-xs uppercase tracking-wide text-slate-200 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((row) => (
                <tr key={row[0]} className="odd:bg-white/[0.02]">
                  <td className="px-3 py-2.5 text-sm text-slate-300 border-b border-white/5 font-medium">{row[0]}</td>
                  {row.slice(1).map((v, i) => <Cell key={i} v={v} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Credits */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5 bg-[#0E131C]" data-testid="pm-credits">
        <SectionTitle kicker="Rodeo Credits" title="One simple currency across Rodeo" sub="Target value: 1 credit ≈ $1. Buy individual services without changing your subscription." />
        <div className="max-w-2xl mx-auto rounded-2xl border border-white/10 bg-[#121823] divide-y divide-white/5">
          {CREDIT_COSTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-5 py-3">
              <span className="text-sm text-slate-300">{k}</span>
              <span className="text-sm font-semibold" style={{ color: GOLD }}>{v}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Competitor following */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5" data-testid="pm-following">
        <div className="max-w-3xl mx-auto rounded-2xl border border-white/10 bg-[#121823] p-7">
          <div className="flex items-center gap-2 text-slate-400 mb-3"><Users className="w-5 h-5" style={{ color: RED }} /><span className="text-xs uppercase tracking-wider font-semibold">Competitor following</span></div>
          <h3 className="font-display text-2xl font-bold text-white">Basic profiles are free. Following is different.</h3>
          <p className="text-sm text-slate-400 mt-2">When you follow a competitor, Rodeo actively tracks them for you: entry info, upcoming rodeos, when they're up, draw, results, standings, available videos and notifications.</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: "rgba(230,179,90,0.12)", color: GOLD }}><Coins className="w-4 h-4" /> 1 competitor = 1 credit / month</div>
        </div>
      </section>

      {/* Sponsorship */}
      <section className="px-5 sm:px-8 py-16 border-t border-white/5 bg-[#0E131C]" data-testid="pm-sponsorship">
        <SectionTitle kicker="Sponsorship & advertising" title="Monetize the audience, not just the subscription"
          sub="Sponsor inventory across both competitor and spectator audiences — pages, draws, videos, profiles, results, leaderboards, stats, notifications, social and weekly recaps." />
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {["Fastest Runs of the Week — Presented by [Sponsor]", "Tie-Down Roping Leaderboard — Presented by [Sponsor]", "Today's Draw — Presented by [Sponsor]", "Performance Report — Presented by [Sponsor]"].map((e) => (
            <div key={e} className="rounded-xl border border-white/10 bg-[#121823] px-4 py-3 text-sm text-slate-300 flex items-center gap-2"><Megaphone className="w-4 h-4 shrink-0" style={{ color: RED }} />{e}</div>
          ))}
        </div>
      </section>
    </div>
  );
}
